import { DestroyRef, inject, Service } from '@angular/core';
import { CustomerService } from './customer.service';

const AUTH_CHANNEL_NAME = 'comet-auth';

const AUTH_SYNC_MESSAGE = {
  signedIn: 'signed-in',
  signedOut: 'signed-out',
} as const;

type AuthSyncMessage = typeof AUTH_SYNC_MESSAGE[keyof typeof AUTH_SYNC_MESSAGE];

/**
 * Carries a sign-in or a sign-out to the other tabs of the same browser.
 *
 * The session lives in a cookie that every tab shares, but each tab holds its own copy of the
 * customer, fetched once on load. Without this, a tab keeps showing the customer as signed in (or as
 * a guest) after another tab has changed that.
 *
 * Applying a message goes through `CustomerService` only, never back through `notify*`, so tabs
 * do not echo each other's messages.
 */
@Service()
export class AuthSyncService {
  private readonly customerService = inject(CustomerService);
  private readonly destroyRef = inject(DestroyRef);

  private channel: BroadcastChannel | null = null;

  /** Browser only: starts listening to the other tabs. */
  init(): void {
    if (this.channel || typeof BroadcastChannel === 'undefined') {
      return;
    }

    this.channel = new BroadcastChannel(AUTH_CHANNEL_NAME);
    this.channel.onmessage = event => this.handle(event.data);
    this.destroyRef.onDestroy(() => this.channel?.close());
  }

  notifySignedIn(): void {
    this.post(AUTH_SYNC_MESSAGE.signedIn);
  }

  notifySignedOut(): void {
    this.post(AUTH_SYNC_MESSAGE.signedOut);
  }

  private post(message: AuthSyncMessage): void {
    this.channel?.postMessage(message);
  }

  private handle(message: AuthSyncMessage): void {
    switch (message) {
      case AUTH_SYNC_MESSAGE.signedIn:
        // Refetched rather than carried in the message: the cookie decides who is signed in, and the
        // customer may have switched accounts rather than gone from a guest to a customer.
        this.customerService.fetchAndSetCustomer().subscribe({ error: () => undefined });
        break;
      case AUTH_SYNC_MESSAGE.signedOut:
        if (this.customerService.isLoggedIn()) {
          this.customerService.setCustomer(null);
        }
        break;
    }
  }
}
